// @ts-nocheck
import React, { useRef, useState } from 'react';
import { generatePDFFingerprint, removePDFFromHistory, clearPDFHistory, loadPDFFile } from './pdfUtils';

interface PDFHistoryItem {
  fingerprint: string;
  fileName: string;
  fileSize: number;
  totalPages: number;
  annotationsCount?: number;
  lastAccessed: string;
}

interface FileUploadProps {
  onUpload: (file: File, fromHistory?: boolean) => void;
  loading: boolean;
  pdfHistory: PDFHistoryItem[];
  onUpdateHistory: () => void;
}

const FileUpload: React.FC<FileUploadProps> = ({ onUpload, loading, pdfHistory, onUpdateHistory }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<PDFHistoryItem | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [loadingHistoryItem, setLoadingHistoryItem] = useState<string | null>(null);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const pdfFile = files.find(file =>
      file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    );
    if (pdfFile) {
      onUpload(pdfFile);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
      onUpload(file);
    } else {
      alert('请选择 PDF 格式的文件');
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleHistoryItemClick = async (historyItem: PDFHistoryItem) => {
    setLoadingHistoryItem(historyItem.fingerprint);

    try {
      const savedFile = await loadPDFFile(historyItem.fingerprint);

      if (savedFile) {
        onUpload(savedFile as unknown as File, true);
      } else {
        setSelectedHistoryItem(historyItem);
        fileInputRef.current?.click();
      }
    } catch (error) {
      console.error('加载历史文件失败:', error);
      setSelectedHistoryItem(historyItem);
      fileInputRef.current?.click();
    } finally {
      setLoadingHistoryItem(null);
    }
  };

  const handleFileForHistory = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || file.type !== 'application/pdf') return;

    if (selectedHistoryItem) {
      const fingerprint = await generatePDFFingerprint(file);
      if (fingerprint === selectedHistoryItem.fingerprint) {
        onUpload(file);
      } else {
        alert('选择的文件与历史记录不匹配，请选择正确的PDF文件');
      }
      setSelectedHistoryItem(null);
    } else {
      onUpload(file);
    }
  };

  const deleteHistoryItem = async (e: React.MouseEvent<HTMLButtonElement>, fingerprint: string) => {
    e.stopPropagation();

    try {
      const success = await removePDFFromHistory(fingerprint);
      if (success) {
        onUpdateHistory();
      } else {
        alert('删除失败，请重试');
      }
    } catch (error) {
      console.error('删除历史记录失败:', error);
      alert('删除失败，请重试');
    }
  };

  const handleClearHistory = () => {
    setShowClearConfirm(true);
  };

  const confirmClearHistory = async () => {
    try {
      const success = await clearPDFHistory();
      if (success) {
        onUpdateHistory();
        setShowClearConfirm(false);
      } else {
        alert('清空失败，请重试');
      }
    } catch (error) {
      console.error('清空历史记录失败:', error);
      alert('清空失败，请重试');
    }
  };

  const cancelClearHistory = () => {
    setShowClearConfirm(false);
  };

  return (
    <div className="upload-container">
      <div
        className={`upload-zone ${loading ? 'loading' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          onChange={selectedHistoryItem ? handleFileForHistory : handleFileSelect}
          style={{ display: 'none' }}
        />

        {loading ? (
          <div className="upload-content">
            <div className="spinner"></div>
            <p>正在处理PDF...</p>
          </div>
        ) : (
          <div className="upload-content">
            <div className="upload-icon">PDF</div>
            <h3>拖放或点击上传 PDF文件</h3>
            <p>支持拖拽文件到此区域或点击选择文件</p>
          </div>
        )}
      </div>

      {pdfHistory.length > 0 && (
        <div className="history-section">
          <div className="history-header">
            <h4>最近打开的文档</h4>
            <button onClick={handleClearHistory} className="clear-history-btn">
              清空历史
            </button>
          </div>
          <div className="history-list">
            {pdfHistory.slice(0, 5).map((item) => (
              <div
                key={item.fingerprint}
                className={`history-item ${loadingHistoryItem === item.fingerprint ? 'loading' : ''}`}
                onClick={() => handleHistoryItemClick(item)}
              >
                <div className="history-item-content">
                  <div className="history-filename">{item.fileName}</div>
                  <div className="history-meta">
                    <span>{Math.round(item.fileSize / 1024)}KB</span>
                    <span>{item.totalPages}页</span>
                    <span>{item.annotationsCount || 0}个标注</span>
                    <span>{new Date(item.lastAccessed).toLocaleDateString()}</span>
                  </div>
                  {loadingHistoryItem === item.fingerprint && (
                    <div className="history-loading">
                      <div className="mini-spinner"></div>
                      <span>加载中...</span>
                    </div>
                  )}
                </div>
                <button
                  className="delete-history-btn"
                  onClick={(e) => deleteHistoryItem(e, item.fingerprint)}
                  disabled={loadingHistoryItem === item.fingerprint}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showClearConfirm && (
        <div className="confirm-overlay">
          <div className="confirm-dialog">
            <h4>确认清空历史记录</h4>
            <p>确定要清空所有历史记录吗？此操作将同时删除相关的标注数据，且无法撤销。</p>
            <div className="confirm-actions">
              <button onClick={cancelClearHistory} className="cancel-btn">
                取消
              </button>
              <button onClick={confirmClearHistory} className="confirm-btn">
                确认清空
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .upload-container {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          min-height: calc(100vh - 80px);
          padding: 2rem;
          gap: 2rem;
        }

        .upload-zone {
          width: 400px;
          height: 300px;
          border: 2px dashed #ccc;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          transition: all 0.3s ease;
          background: white;
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }

        .upload-zone:hover {
          border-color: #007bff;
          background-color: #f8f9fa;
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(0,0,0,0.15);
        }

        .upload-zone.loading {
          cursor: not-allowed;
          opacity: 0.7;
        }

        .upload-content {
          text-align: center;
        }

        .upload-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
          color: #007bff;
        }

        .upload-content h3 {
          margin: 0 0 0.5rem 0;
          color: #333;
          font-size: 1.2rem;
        }

        .upload-content p {
          margin: 0;
          color: #666;
          font-size: 14px;
        }

        .spinner {
          border: 3px solid #f3f3f3;
          border-top: 3px solid #007bff;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem auto;
        }

        .mini-spinner {
          border: 2px solid #f3f3f3;
          border-top: 2px solid #007bff;
          border-radius: 50%;
          width: 16px;
          height: 16px;
          animation: spin 1s linear infinite;
          margin-right: 0.5rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .history-section {
          width: 100%;
          max-width: 600px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          overflow: hidden;
        }

        .history-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          background: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
        }

        .history-header h4 {
          margin: 0;
          color: #333;
          font-size: 1.1rem;
        }

        .clear-history-btn {
          background: #dc3545;
          color: white;
          border: none;
          padding: 0.4rem 0.8rem;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
          transition: background 0.2s;
        }

        .clear-history-btn:hover {
          background: #c82333;
        }

        .history-list {
          max-height: 300px;
          overflow-y: auto;
        }

        .history-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #f0f0f0;
          cursor: pointer;
          transition: background 0.2s;
          position: relative;
        }

        .history-item:hover:not(.loading) {
          background: #f8f9fa;
        }

        .history-item.loading {
          cursor: wait;
          background: #f8f9fa;
        }

        .history-item:last-child {
          border-bottom: none;
        }

        .history-item-content {
          flex: 1;
          position: relative;
        }

        .history-filename {
          font-weight: 500;
          color: #333;
          margin-bottom: 0.3rem;
          font-size: 14px;
        }

        .history-meta {
          display: flex;
          gap: 1rem;
          font-size: 12px;
          color: #666;
        }

        .history-loading {
          position: absolute;
          top: 50%;
          right: 2rem;
          transform: translateY(-50%);
          display: flex;
          align-items: center;
          font-size: 12px;
          color: #666;
        }

        .delete-history-btn {
          background: none;
          border: none;
          color: #999;
          font-size: 18px;
          cursor: pointer;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.2s;
        }

        .delete-history-btn:hover:not(:disabled) {
          background: #f0f0f0;
          color: #dc3545;
        }

        .delete-history-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .confirm-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1001;
        }

        .confirm-dialog {
          background: white;
          border-radius: 8px;
          padding: 1.5rem;
          width: 90%;
          max-width: 400px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.2);
        }

        .confirm-dialog h4 {
          margin: 0 0 1rem 0;
          color: #333;
          font-size: 1.1rem;
        }

        .confirm-dialog p {
          margin: 0 0 1.5rem 0;
          color: #666;
          line-height: 1.4;
        }

        .confirm-actions {
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
        }

        .cancel-btn {
          background: #6c757d;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .cancel-btn:hover {
          background: #5a6268;
        }

        .confirm-btn {
          background: #dc3545;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .confirm-btn:hover {
          background: #c82333;
        }

        @media (max-width: 768px) {
          .upload-container {
            padding: 1rem;
          }

          .upload-zone {
            width: 90%;
            max-width: 350px;
            height: 250px;
          }

          .history-section {
            width: 90%;
          }

          .history-header {
            padding: 0.75rem 1rem;
          }

          .history-item {
            padding: 0.75rem 1rem;
          }

          .history-meta {
            flex-wrap: wrap;
            gap: 0.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default FileUpload;
